import { Box, colors, styled, Theme, Typography } from '@mui/material'
import { SmepEventHealthEnum, SmepEventStateEnum } from '@nativewaves/nw-cloud-sdk/smep'

import { UpcomingProximity } from 'domains/Workspaces/Events/upcomingProximity'
import { ThemeTypes } from 'stores/settings'
import { getStateColor } from 'utils'

export const Thumbnail = styled('object')(({ theme }) => ({
  width: 64,
  height: 36,
  aspectRatio: '16 / 9',
  objectFit: 'contain',
  borderRadius: theme.shape.borderRadius / 2,
  display: 'grid',
  placeItems: 'center',
  backgroundColor: theme.palette.background.levels[500],
}))

export const NoSrcPlaceholderWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.levels[500],
  width: 64,
  height: 36,
  borderRadius: theme.shape.borderRadius / 2,
  display: 'grid',
  placeItems: 'center',
  '& > svg': {
    width: 'auto',
    height: 24,
  },
}))

/**
 * Colors for an upcoming event whose start is near. Deliberately louder than
 * the near-background `Upcoming` shade — these are the rows worth spotting
 * while scrolling.
 */
const getUpcomingProximityColor = (proximity: UpcomingProximity, mode: Theme['palette']['mode']) => {
  const shade = mode === ThemeTypes.DARK ? 700 : 300

  switch (proximity) {
    case 'imminent':
      return colors.orange[shade]
    case 'today':
      return colors.amber[shade]
  }
}

type StateVisualizerProps = {
  state: SmepEventStateEnum
  health: SmepEventHealthEnum
  /** Set for an upcoming event starting today or sooner — see `getUpcomingProximity`. */
  proximity?: UpcomingProximity | null
}

export const StateVisualizer = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'state' && prop !== 'health' && prop !== 'proximity',
})<StateVisualizerProps>(({ theme, state, health, proximity }) => {
  // Health outranks proximity the same way it outranks state inside `getStateColor`: an event
  // an hour out with a failing pre-flight is the one row that must not read as merely soon.
  const backgroundColor =
    proximity && health !== SmepEventHealthEnum.Warning && health !== SmepEventHealthEnum.Critical
      ? getUpcomingProximityColor(proximity, theme.palette.mode)
      : getStateColor(state, theme.palette.mode, health)

  return {
    width: 80,
    textAlign: 'center',
    display: 'inline-block',
    padding: theme.spacing(0.3),
    margin: theme.spacing(0),
    backgroundColor,
    color: theme.palette.getContrastText(backgroundColor),
    borderRadius: theme.shape.borderRadius,
    // The label carries a relative time and an icon here, so it grows past the fixed state width.
    ...(proximity && {
      width: 'auto',
      minWidth: 80,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(0.5),
      paddingInline: theme.spacing(0.75),
      fontWeight: theme.typography.fontWeightMedium,
      '& > svg': {
        fontSize: '1.3em',
      },
    }),
  }
})
